import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  videoService,
  type AcceptFindingPayload,
  type Clip,
  type ClipStreamUrl,
  type CreateClipPayload,
  type Finding,
  type UpdateFindingPayload,
  type VideoSource,
  type VideoStreamUrl,
} from '../services/video.service';

export function useVideoSource(id: string | null | undefined) {
  return useQuery<VideoSource>({
    queryKey: ['video-source', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('video_source_id is required'));
      return videoService.getVideoSource(id);
    },
    enabled: !!id,
    // Poll while ANY async work is in flight. Re-evaluated after every fetch
    // (so once a status flips back to 'generating' we resume polling) and
    // continues even when the tab is backgrounded.
    refetchInterval: (query) => {
      const v = query.state.data;
      if (!v) return 5_000;
      const mediaDone = v.status === 'ready' || v.status === 'failed';
      const chaptersDone =
        v.chapters_status === 'ready' ||
        v.chapters_status === 'failed' ||
        v.chapters_status === 'not_requested';
      const findingsDone =
        v.findings_status === 'ready' ||
        v.findings_status === 'failed' ||
        v.findings_status === 'not_requested';
      if (mediaDone && chaptersDone && findingsDone) return false;
      return 2_000;
    },
    refetchIntervalInBackground: true,
    // Always refetch on (re)mount so a tool result that just flipped a status
    // server-side picks up the new state immediately, instead of returning
    // a cached "done" value and stopping the polling loop.
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

export function useCreateClip() {
  const queryClient = useQueryClient();
  return useMutation<Clip, Error, CreateClipPayload>({
    mutationFn: (payload) => videoService.createClip(payload),
    onSuccess: (_clip, vars) => {
      queryClient.invalidateQueries({ queryKey: ['video-source', vars.video_source_id] });
    },
  });
}

export function useVideoStreamUrl(id: string | null | undefined, opts?: { enabled?: boolean }) {
  return useQuery<VideoStreamUrl>({
    queryKey: ['video-stream-url', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('video_source_id is required'));
      return videoService.getVideoStreamUrl(id);
    },
    enabled: !!id && opts?.enabled !== false,
    // Signed URLs expire (~30 min). Refresh before then.
    staleTime: 20 * 60 * 1000,
    retry: (count, err: any) => {
      if (err?.code === 409) return false;
      return count < 2;
    },
  });
}

export function useUpdateFinding() {
  const queryClient = useQueryClient();
  return useMutation<
    Finding,
    Error,
    { videoSourceId: string; findingId: string; patch: UpdateFindingPayload }
  >({
    mutationFn: ({ videoSourceId, findingId, patch }) =>
      videoService.updateFinding(videoSourceId, findingId, patch),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['video-source', vars.videoSourceId] });
    },
  });
}

export function useAcceptFinding() {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof videoService.acceptFinding>>,
    Error,
    { videoSourceId: string; findingId: string; payload: AcceptFindingPayload }
  >({
    mutationFn: ({ videoSourceId, findingId, payload }) =>
      videoService.acceptFinding(videoSourceId, findingId, payload),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['video-source', vars.videoSourceId] });
    },
  });
}

export function useDeclineFinding() {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof videoService.declineFinding>>,
    Error,
    { videoSourceId: string; findingId: string }
  >({
    mutationFn: ({ videoSourceId, findingId }) =>
      videoService.declineFinding(videoSourceId, findingId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['video-source', vars.videoSourceId] });
    },
  });
}

export function useClip(id: string | null | undefined) {
  return useQuery<Clip>({
    queryKey: ['clip', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('clip_id is required'));
      return videoService.getClip(id);
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useClipStreamUrl(id: string | null | undefined, opts?: { enabled?: boolean }) {
  return useQuery<ClipStreamUrl>({
    queryKey: ['clip-stream-url', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('clip_id is required'));
      return videoService.getClipStreamUrl(id);
    },
    enabled: !!id && opts?.enabled !== false,
    // Signed URLs expire (~30 min server-side). Refresh proactively.
    staleTime: 20 * 60 * 1000,
    retry: (count, err: any) => {
      // 409 = video not ready yet — don't hammer the endpoint; the parent
      // useVideoSource hook is polling status and will trigger a refetch
      // when it flips.
      if (err?.code === 409) return false;
      return count < 2;
    },
  });
}
